import { asError } from '#/lib/asError';
import { filterSortBookingSummaries } from '#/lib/filterSortBookingSummaries';
import type { Claims } from '@auth0/nextjs-auth0';
import { CACHE_TTL, cache } from '@jucy-nakuru/common/lib/cache';
import { defaultHeadersMiddleware } from '@jucy-nakuru/common/lib/defaultHeadersMiddleware';
import RentalsReservation, { RentalsReservationInstance } from '@jucy-nakuru/common/services/JucyRentalsApi/RentalsReservation';
import clientConfig from '@jucy-nakuru/config/clientConfig';
import serverConfig from '@jucy-nakuru/config/serverConfig';
import { Configuration, SelfServiceApi } from '@jucy/rentals-api-client';
import { BookingSummary, Configuration as V3Configuration, SelfServiceApi as V3SelfServiceApi } from '@jucy/rentals-api-client/rentals-api-v3';
import type { LogSelfServiceEventRequest } from '@jucy/rentals-api-client/rentals-api-v3/models';
import jwt_decode from 'jwt-decode';
import ErrorReporter from '../lib/ErrorReporter';
import { LoginCookieHandler } from '../lib/LoginCookieHandler';
import promiseRetry from '../lib/promiseRetry';
import { getSelfServiceSessionId } from '../lib/selfServiceSession';

export const createSelfServiceApi = (accessToken: string): RentalSelfServiceApiClient =>
    new RentalSelfServiceApiClient(
        new Configuration({
            basePath: clientConfig.rentalsApi.baseUrl,
            accessToken,
        })
    );

export class RentalSelfServiceApiClient extends SelfServiceApi {
    v3: V3SelfServiceApi;

    constructor(configuration?: Configuration) {
        const defaultConfig = new Configuration({
            basePath: configuration?.basePath || clientConfig.rentalsApi.baseUrl,
            fetchApi: configuration?.fetchApi,
            accessToken: configuration?.accessToken as never,
            middleware: [defaultHeadersMiddleware, ...(configuration?.middleware || [])],
        });

        super(defaultConfig);
        this.v3 = new V3SelfServiceApi(defaultConfig as unknown as V3Configuration);
    }

    logEvent(reservationRef: string, logSelfServiceEventRequest: LogSelfServiceEventRequest) {
        const request = {
            branchCode: LoginCookieHandler.getLoginAtBranchCode(),
            sessionId: getSelfServiceSessionId(),
            ...logSelfServiceEventRequest,
        };
        promiseRetry(() => rentalSelfServiceApiClient.v3.logSelfServiceEvent(reservationRef, request)).catch((error) => {
            ErrorReporter.reportError({
                error: asError(error),
                tags: {
                    location: 'rentalSelfServiceApiClient',
                },
            });
        });
    }

    async getReservation(reservationReference: string): Promise<RentalsReservationInstance> {
        const rawReservation = await this.selfServiceGetReservation(reservationReference);
        return new RentalsReservation(rawReservation) as RentalsReservationInstance;
    }

    async getEmailFromToken(): Promise<string | undefined> {
        try {
            const token = await this.configuration.accessToken?.();
            const decoded = token ? jwt_decode<Claims>(token) : undefined;
            const user = decoded ? decoded?.[`${serverConfig.auth0.claimsNamespace}/user`] : undefined;
            if (typeof user?.email === 'string') {
                return user?.email;
            }
        } catch (error) {
            ErrorReporter.reportError(error);
        }
    }

    async fullBookingSummaries({ refresh }: { refresh: boolean } = { refresh: false }): Promise<BookingSummary[]> {
        const getBookingSummaries = async () => {
            const bookingSummaries = await this.v3.getSelfServiceSummaries().then((s) => s.items);

            return filterSortBookingSummaries(bookingSummaries) || [];
        };

        const email = await this.getEmailFromToken();
        if (!email) {
            return getBookingSummaries();
        }

        const cacheKey = `summaries-${Buffer.from(email).toString('base64')}`;
        let result = refresh ? null : await cache.get<BookingSummary[]>(cacheKey);
        if (!result) {
            result = await getBookingSummaries();
        }
        await cache.set(cacheKey, result, CACHE_TTL.FIVE_MIN);

        return result || [];
    }
}

const rentalSelfServiceApiClient = new RentalSelfServiceApiClient();
export default rentalSelfServiceApiClient;
